import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Input, Select, Table } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import type { ApplicationStatus } from '@/lib/intake/api';
import { formatDate } from '@/lib/format';
import { type Problem, toProblem } from '@/lib/problem';
import { type ListPage, review, type Search, STATUS_LABELS, statusLabel, statusTone } from './api';
import styles from './review.module.css';

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Every status' },
  ...(['SUBMITTED', 'IN_REVIEW', 'INFORMATION_REQUESTED', 'CLOSED'] as const).map((s) => ({
    value: s,
    label: STATUS_LABELS[s],
  })),
];

/** The back office's list: every submitted application, newest first, to be found and opened. */
export function ApplicationsPage() {
  const [search, setSearch] = useState<Search>({ status: null, text: '', page: 0 });
  const [typed, setTyped] = useState('');
  const [found, setFound] = useState<ListPage | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);

  useEffect(() => {
    let cancelled = false;
    review
      .search(search)
      .then((page) => {
        if (cancelled) return;
        setFound(page);
        setProblem(null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(toProblem(error));
      });
    return () => {
      cancelled = true;
    };
  }, [search]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSearch((s) => ({ ...s, text: typed.trim(), page: 0 }));
  };

  const pages = found ? Math.max(1, Math.ceil(found.total / found.pageSize)) : 1;

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h1 className={styles.title}>Applications</h1>
        <p className={styles.muted}>
          Every application sent to the advisory team, newest first. Opening one is recorded in its trail.
        </p>
      </div>

      <form className={styles.filters} onSubmit={submit} role="search">
        <Input
          label="Find"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          helpText="A reference, or part of a name, company or email address"
        />
        <Select
          label="Status"
          value={search.status ?? ''}
          options={FILTERS}
          onChange={(e) =>
            setSearch((s) => ({ ...s, status: (e.target.value || null) as ApplicationStatus | null, page: 0 }))
          }
        />
        <div className={styles.filterButton}>
          <Button type="submit">Search</Button>
        </div>
      </form>

      {problem && <ProblemAlert problem={problem} title="The applications could not be loaded" />}

      {found && (
        <section aria-labelledby="results">
          <h2 id="results" className={styles.sectionTitle}>
            {found.total === 1 ? '1 application' : `${found.total.toLocaleString('en-US')} applications`}
          </h2>
          <Table
            caption="Applications sent to the advisory team"
            emptyMessage="No application matches."
            columns={[
              { key: 'reference', header: 'Reference', nowrap: true },
              { key: 'applicant', header: 'Applicant' },
              { key: 'status', header: 'Status', nowrap: true },
              { key: 'submitted', header: 'Sent', nowrap: true },
              { key: 'gaps', header: 'Incomplete sections', numeric: true },
              { key: 'files', header: 'Files', numeric: true },
            ]}
            rows={found.items.map((item) => ({
              key: item.applicationId,
              cells: {
                reference: (
                  <Link to={`/applications/${item.applicationId}`} className={styles.reference}>
                    {item.reference}
                  </Link>
                ),
                applicant: (
                  <span className={styles.stack}>
                    <span>{item.applicantName ?? 'Name not given'}</span>
                    <span className={styles.muted}>{item.applicantEmail}</span>
                  </span>
                ),
                status: <Badge tone={statusTone(item.status)}>{statusLabel(item.status, item.outcome)}</Badge>,
                submitted: formatDate(item.submittedAt),
                gaps: item.incompleteSections,
                files: item.files,
              },
            }))}
          />
          {pages > 1 && (
            <nav className={styles.pager} aria-label="Pages of applications">
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page === 0}
                onClick={() => setSearch((s) => ({ ...s, page: s.page - 1 }))}
              >
                Previous
              </Button>
              <span className={styles.muted}>
                Page {search.page + 1} of {pages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page + 1 >= pages}
                onClick={() => setSearch((s) => ({ ...s, page: s.page + 1 }))}
              >
                Next
              </Button>
            </nav>
          )}
        </section>
      )}
    </div>
  );
}
